import { lazy, Suspense, useState, type ReactElement } from 'react';
import { Box, Button, Group, Loader, Tabs, Text } from '@mantine/core';
import { RotateCcw, Sparkles } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import type { AssistContext } from '@/shared/assist/AssistContract';
import type { AnalyticsField } from '@/shared/analytics/AnalyticsTypes';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters, useAssistSurface } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import { AIExplorerViewModel } from './AIExplorerViewModel';
import { findLayout } from './AIExplorerLayouts';
import AIExplorerLayoutGrid from './AIExplorerLayoutGrid';
import AIExplorerStarter from './AIExplorerStarter';
import { readViewMode, writeViewMode, type AIExplorerViewMode } from './AIExplorerViewMode';

const AIExplorerPlayground = lazy(async () => import('./AIExplorerPlayground'));

interface AIExplorerPageProps {
    surfaceId: string;
}

/**
 * The dynamic dashboard page (the former "studio", now the AI Explorer landing). It
 * composes the standard chrome — AMA toolbar, ambient filter row, and a
 * (dynamically-configured) title — and starts from a set of cycling starter
 * prompts; once a dashboard is composed it streams in a tile layout and offers
 * "Start over" to return to the starter.
 */
export default function AIExplorerPage({ surfaceId }: AIExplorerPageProps): ReactElement {
    const surface = useSurface(surfaceId);
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <DashboardBody surface={surface} />
        </SurfaceAssistScope>
    );
}

function DashboardBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const vm = useViewModel(AIExplorerViewModel);
    const [mode, setMode] = useState<AIExplorerViewMode>(readViewMode);

    const changeMode = (value: string | null): void => {
        const next: AIExplorerViewMode = value === 'dashboards' ? 'dashboards' : 'playground';
        setMode(next);
        writeViewMode(next);
    };

    const title = vm.title ?? surface.title;
    const subtitle = vm.subtitle ?? surface.subtitle;

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <PageHeaderComponent icon={Sparkles} eyebrow="Playground" title={title} subtitle={subtitle} />

            <Tabs value={mode} onChange={changeMode} mb="md">
                <Tabs.List>
                    <Tabs.Tab value="playground">Playground</Tabs.Tab>
                    <Tabs.Tab value="dashboards">Dashboards</Tabs.Tab>
                </Tabs.List>
            </Tabs>

            {mode === 'playground' ? (
                <Suspense
                    fallback={
                        <Group justify="center" py="xl">
                            <Loader size="sm" />
                        </Group>
                    }
                >
                    <AIExplorerPlayground />
                </Suspense>
            ) : (
                <VisualBody surface={surface} />
            )}
        </Box>
    );
}

function VisualBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const vm = useViewModel(AIExplorerViewModel);
    const filters = useAssistFilters();
    const controller = useAssistSurface();

    const layout = vm.layoutId !== null ? findLayout(vm.layoutId) : undefined;
    const composing = vm.status === 'composing';

    const handleCompose = (prompt: string): void => {
        const context: AssistContext = { surfaceId: surface.id, surfaceTitle: surface.title, activeFilters: filters };
        vm.compose(prompt, context);
    };

    const handleDrill = (field: AnalyticsField, value: string): void => {
        controller?.addFilter(field, value);
    };

    return (
        <Box>
            {vm.error !== null && (
                <Text size="sm" c="red" mb="md">
                    {vm.error}
                </Text>
            )}

            {layout !== undefined ?
                <>
                    <Group justify="space-between" align="center" mb="md" wrap="nowrap" gap="sm">
                        <Text size="sm" c="dimmed" truncate style={{ minWidth: 0 }}>
                            {vm.lastPrompt.length > 0 ? `Composed from “${vm.lastPrompt}”` : 'Your composed dashboard'}
                        </Text>
                        <Button variant="subtle" color="gray" size="compact-sm" leftSection={<RotateCcw size={14} />} onClick={() => vm.reset()} style={{ flexShrink: 0 }}>
                            Start over
                        </Button>
                    </Group>
                    <AIExplorerLayoutGrid layout={layout} slotIds={vm.slotIds} tiles={vm.tiles} pageFilters={filters} basePath={surface.basePath} onDrill={handleDrill} />
                </>
            : composing ?
                <Group justify="center" gap="sm" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                        Composing your dashboard…
                    </Text>
                </Group>
            :   <AIExplorerStarter onCompose={handleCompose} composing={composing} />}
        </Box>
    );
}
